import { useState } from 'react';
import { createAIAgent, updateAIAgent, searchAIAgents } from '../../services/aiAgentService';
import type { RetellAIAgent, ContextDataField } from '../../types/aiAgent';

type View = 'main' | 'contextItem';

interface AIAgentModalProps {
  agent?: RetellAIAgent;
  onClose: () => void;
  onSuccess: () => void;
}

export function AIAgentModal({ agent, onClose, onSuccess }: AIAgentModalProps) {
  const isEdit = !!agent;

  // Main form state
  const [retellAgentId, setRetellAgentId] = useState(agent?.retellAgentId || '');
  const [active, setActive] = useState(agent?.active ?? true);
  const [name, setName] = useState(agent?.name || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Navigation state
  const [view, setView] = useState<View>('main');

  // Context data state
  const [contextData, setContextData] = useState<ContextDataField[]>(
    agent?.contextData ? JSON.parse(JSON.stringify(agent.contextData)) : [],
  );
  const [editingContextIndex, setEditingContextIndex] = useState<number | null>(null);
  const [ctxKey, setCtxKey] = useState('');
  const [ctxLabel, setCtxLabel] = useState('');
  const [ctxDefault, setCtxDefault] = useState('');

  // ── Context data helpers ─────────────────────────────────────────────────

  function openContextForm(index: number | null) {
    if (index !== null) {
      const item = contextData[index];
      setCtxKey(item.key);
      setCtxLabel(item.label);
      setCtxDefault(item.default || '');
    } else {
      setCtxKey('');
      setCtxLabel('');
      setCtxDefault('');
    }
    setEditingContextIndex(index);
    setView('contextItem');
  }

  function saveContextItem() {
    if (!ctxKey.trim() || !ctxLabel.trim()) return;
    const item: ContextDataField = {
      key: ctxKey.trim(),
      label: ctxLabel.trim(),
      default: ctxDefault.trim() || undefined,
    };
    const updated = [...contextData];
    if (editingContextIndex !== null) {
      updated[editingContextIndex] = item;
    } else {
      updated.push(item);
    }
    setContextData(updated);
    setView('main');
  }

  function deleteContextItem(index: number) {
    setContextData(contextData.filter((_, i) => i !== index));
  }

  // ── Save ──────────────────────────────────────────────────────────────────

  async function handleSave() {
    if (!retellAgentId.trim() || !name.trim()) return;
    setError('');
    setLoading(true);
    try {
      if (isEdit) {
        await updateAIAgent(agent!.id, {
          ...agent!,
          retellAgentId: retellAgentId.trim(),
          active,
          name: name.trim(),
          contextData,
        });
      } else {
        // Check if an AI agent already exists with this Retell Agent Id
        const existing = await searchAIAgents({}, { pageSize: 1000 });
        if (existing.some((a) => a.retellAgentId === retellAgentId.trim())) {
          setError('An AI agent with this Retell Agent Id already exists.');
          setLoading(false);
          return;
        }
        await createAIAgent({
          retellAgentId: retellAgentId.trim(),
          active,
          name: name.trim(),
          contextData: contextData.length > 0 ? contextData : undefined,
        });
      }
      onSuccess();
    } catch {
      setError(isEdit ? 'Failed to update AI agent' : 'Failed to create AI agent');
    } finally {
      setLoading(false);
    }
  }

  // ── Shared styles ─────────────────────────────────────────────────────────

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const btnSmall = 'text-xs px-2 py-1 rounded transition-colors';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">

        {/* ── Main Form ─────────────────────────────────────────────── */}
        {view === 'main' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {isEdit ? 'Edit AI Agent' : 'Add AI Agent'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Retell Agent Id *</label>
                  <input type="text" value={retellAgentId} onChange={(e) => setRetellAgentId(e.target.value)} required className={inputClass} placeholder="e.g. agent_40925e8593d317fc8127899599" />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="rounded border-gray-300" />
                  Active
                </label>
                <div>
                  <label className={labelClass}>Name *</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} required className={inputClass} placeholder="Agent name" />
                </div>

                {/* Context Data */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelClass}>Context Data</label>
                    <button type="button" onClick={() => openContextForm(null)} className={`${btnSmall} text-blue-600 hover:bg-blue-50`}>+ Add</button>
                  </div>
                  {contextData.length === 0 ? (
                    <p className="text-sm text-gray-400">No context data yet</p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {contextData.map((item, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2">
                          <span className="text-sm text-gray-700">{item.key}</span>
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => openContextForm(i)} className={`${btnSmall} text-gray-500 hover:bg-gray-100`}>Edit</button>
                            <button type="button" onClick={() => deleteContextItem(i)} className={`${btnSmall} text-red-500 hover:bg-red-50`}>Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
              <button type="button" onClick={handleSave} disabled={loading || !retellAgentId.trim() || !name.trim()} className={btnPrimary}>
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}

        {/* ── Context Data Item Form ──────────────────────────────────── */}
        {view === 'contextItem' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {editingContextIndex !== null ? 'Edit Context Item' : 'Add Context Item'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Key *</label>
                  <input type="text" value={ctxKey} onChange={(e) => setCtxKey(e.target.value)} required className={inputClass} placeholder="e.g. budget" />
                </div>
                <div>
                  <label className={labelClass}>Label *</label>
                  <input type="text" value={ctxLabel} onChange={(e) => setCtxLabel(e.target.value)} required className={inputClass} placeholder="e.g. Budget" />
                </div>
                <div>
                  <label className={labelClass}>Default</label>
                  <input type="text" value={ctxDefault} onChange={(e) => setCtxDefault(e.target.value)} className={inputClass} placeholder="Optional default value" />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setView('main')} className={btnOutline}>Back</button>
              <button type="button" onClick={saveContextItem} disabled={!ctxKey.trim() || !ctxLabel.trim()} className={btnPrimary}>Done</button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
